import React, { useEffect, useMemo, useState } from 'react';
import { deleteUserInventoryMapping, fetchAllUserInventoryMapping } from 'services/api/userInventoryMapping';
import { Loading } from 'components/loading/Loading';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import DataTable from 'react-data-table-component';
import { USER_ROLES, isUserAuthorized, showError, showSuccess } from 'utils';
import { tableStyles } from 'css/tableStyles';
import CustomLink from 'components/TableLink';
import AddUserInventoryItemModal from 'components/AddUserInventoryItemModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import EditActionForTable from 'components/EditActionForTableData';

const selectedUserMappingDefaultState = { id:'',originalDiscountPercent:0,originalFixedDiscountAmount:0 };
const UserDetails = props => {
  const { inventoryItemId, userRoles } = props;
  const isUserAuthorizedToEdit = isUserAuthorized(userRoles, [USER_ROLES.PACKER, USER_ROLES.SUPERVISOR, USER_ROLES.MANAGER, USER_ROLES.ADMIN]);

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalUpdateOpen, setModalUpdateOpen] = useState<boolean>(false);
  const [itemSelected, setItemSelected] = useState<string>('');
  const [selectedUserMapping,setSelectedUserMapping]= useState<Record<string,any>>(selectedUserMappingDefaultState);
  const fetchUsersInventoryFn = async () => {
    try {
      setLoading(true);
      const response = await fetchAllUserInventoryMapping({
        inventoryItemId,
      });
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersInventoryFn();
  }, []);

  const deleteUserInventoryMappingFn = async (id: string) => {
    try {
      await deleteUserInventoryMapping(id);
      await fetchUsersInventoryFn();
      showSuccess('Inventory Item removed successfully.');
    } catch (e) {
      showError(e);
    }
  };

  const editUserInventoryMappingFn = (row) => {
    setItemSelected(row.user.objectId);
    setSelectedUserMapping({
      id:row.objectId,
      originalDiscountPercent:row.discountPercent,
      originalFixedDiscountAmount:row.fixedDiscountAmount,
    });
    setModalUpdateOpen(true);
  };

  const renderRowAction=(row)=>{
    return (
      <div className={'d-flex ml-1'}>
        <div>
          <EditActionForTable
            onClick={() => editUserInventoryMappingFn(row)}
          />
        </div>
        <div className={'ml-4'}>
          <DeleteActionForTable
            onClick={() => deleteUserInventoryMappingFn(row.objectId)}
          />
        </div>
      </div>
    );
  };
  const columns = useMemo(() => [
    {
      name: 'UserId',
      selector: row => row.user.objectId,
      sortable: true,
      cell: row => (
        <CustomLink
          text={row.user.objectId}
          linkTo={`/users/${row.user.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: row => row.user.full_name,
      sortable: true,
    },
    {
      name: 'Phone No',
      selector: row => row.user.phone,
      sortable: false,
    },
    {
      name: 'Email',
      selector: row => row.user.username,
      sortable: false,
    },
    {
      name: 'Discount Percent',
      selector: row => row.discountPercent,
      sortable: true,
    },
    {
      name: 'Fixed Discount Amount',
      selector: row => row.fixedDiscountAmount,
      sortable: true,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      width: '100px',
      omit: !isUserAuthorizedToEdit,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        renderRowAction(row) 
      ),
    },
  ], []);

  if (loading) return (
    <div className={styles['loader-container']}>
      <Loading type={'mini'} />
    </div>
  );

  return (
    <div className={styles['user-inventory-container']}>
      {modalOpen && (
        <AddUserInventoryItemModal
          inventoryItemId={inventoryItemId}
          isUser={true}
          selectedUserMapping={{}}
          onSuccess={fetchUsersInventoryFn}
          toggleModal={() => setModalOpen(!modalOpen)}
        />
      )}
      {modalUpdateOpen && (
        <AddUserInventoryItemModal
          isEdit={true}
          selectedItemToUpdate={itemSelected}
          selectedUserMapping= {selectedUserMapping}
          onSuccess={fetchUsersInventoryFn}
          toggleModal={() => setModalUpdateOpen(!modalUpdateOpen)}
        />
      )}
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Inventory User Mapping</div>
        {isUserAuthorizedToEdit && <ActionButton
          active={true}
          isLoading={false}
          onClick={() => setModalOpen(true)}
          text={<>User <span className={'ml-3'}>+</span></>}
        />}
      </div>
      <DataTable
        columns={columns}
        noHeader={true}
        data={data}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField='objectId'
      />
    </div>
  );
};

export default UserDetails;